import {BaseDashlet} from "../../lq_js_common/dashboard/base_dashlet";
import {DashboardGraph} from "../../graphs/dashboard_graph";
import {GraphOptionsBuilder} from "../../lq_js_common/e_charts/chart_builder";
import {createBoundedAxisTooltip, formatMillisecondsAxis, formatTimeTick} from "../../helpers/chart_common";
import {isRedacted} from "../../helpers/redact";
import {getTopLevelItems, parseShaperChildrenBlob} from "../../helpers/shaper_children";
import {debugLog} from '../../helpers/debug_log';

export class ShaperCombinedRtt extends BaseDashlet {
    constructor(slot) {
        super(slot);
        this.rendered = false;
        this.lastGraphState = null;
    }

    title() {
        return "Shaper Child TCP Round-Trip Time";
    }

    supportsZoom() {
        return true;
    }

    tooltip() {
        return "<h5>Shaper Combined Round-Trip Time</h5><p>Child site TCP retransmits.</p>";
    }

    buildContainer() {
        let base = super.buildContainer();
        base.appendChild(this.graphDiv());
        return base;
    }

    setup() {
        super.setup();
        this.graph = new ShaperCombinedRetrrGraph(this.graphDivId());
        if (this.lastGraphState !== null) {
            this.graph.update(this.lastGraphState);
        }
    }

    channelRequests() {
        return [ "SiteShaperPage" ];
    }

    onMessage(msg) {
        if (msg.Data.target !== "shaper_children") return;

        if (!this.rendered) {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("card", "animate__animated", "animate__fadeIn");
        } else {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("animate__animated", "animate__pulse");
            setTimeout(() => {
                myDiv.classList.remove("animate__animated", "animate__pulse");
            }, 1000);
        }
        const parsed = parseShaperChildrenBlob(msg.Data.blob);
        window.isFlatNetwork = parsed.isFlat;
        this.graph.update(parsed);
        this.lastGraphState = parsed;
    }

}

class ShaperCombinedRetrrGraph extends DashboardGraph {
    constructor(id) {
        super(id);
        this.option = new GraphOptionsBuilder()
            .withLeftGridSize(75)
            .withTimeAxis()
            .withScaledAbsYAxis("RTT (ms)", 50)
            .withEmptyLegend()
            .withEmptySeries()
            .build();
        this.option.yAxis.axisLabel.formatter = formatMillisecondsAxis;
        this.option && this.chart.setOption(this.option);
    }

    update(payload) {
        this.chart.hideLoading();

        const dataset = getTopLevelItems(payload);
        const sites = dataset.topLevel || {};
        const topSet = new Set([...(dataset.topDownload || []), ...(dataset.topUpload || [])]);

        let series = [];
        let otherDown = [];
        let otherCount = [];

        const legends = new Set();
        this.option.legend.data = [];
        this.option.xAxis.data = [];

        let n = 0;
        const addLegend = (name) => {
            if (!legends.has(name)) {
                legends.add(name);
                this.option.legend.data.push(name);
            }
        };

        const shouldInclude = (site) => {
            if (!topSet.size) return true;
            return topSet.has(site);
        };

        for (const [site, value] of Object.entries(sites)) {
            if (!this.option.xAxis.data.length) {
                this.option.xAxis.data = value.body.ticks || [];
            }
            const name = value.header.name;
            const rttSeries = (value.body.median_rtt_median || []).map((v) => (v || 0) / 10);

            if (shouldInclude(site)) {
                series.push({
                    name: name,
                    type: "line",
                    data: rttSeries,
                    itemStyle: {
                        color: window.graphPalette[n]
                    },
                    smooth: true,
                    showSymbol: false
                });
                addLegend(name);
                n += 1;
            } else {
                for (let i=0; i<rttSeries.length; i++) {
                    if (otherDown.length <= i) {
                        otherDown.push(0);
                        otherCount.push(0);
                    }
                    otherDown[i] += rttSeries[i];
                    otherCount[i] += 1;
                }
            }
        }

        if (otherDown.length > 0) {
            const averaged = otherDown.map((sum, idx) => {
                const count = otherCount[idx] || 1;
                return count ? sum / count : 0;
            });
            series.push({
                name: "Other",
                type: "line",
                data: averaged,
                itemStyle: {
                    color: window.graphPalette[n]
                },
                smooth: true,
                showSymbol: false
            });
            addLegend("Other");
        }

        this.option.series = series;
        
        // Apply redaction to legend if enabled
        if (isRedacted()) {
            this.option.legend.textStyle = {
                fontFamily: "Illegible"
            };
        } else {
            this.option.legend.textStyle = {};
        }
        
        this.option.tooltip = createBoundedAxisTooltip({
            yFormatter: formatMillisecondsAxis,
            formatter: (params) => {
                let result = `<div style="font-size: 10px;">${formatTimeTick(params[0].axisValue)}<br/><strong>Round-trip time</strong><br/>`;
                const sortedParams = params.slice().sort((a, b) => b.value - a.value);
                sortedParams.forEach((param) => {
                    if (param.value !== undefined && param.value !== null) {
                        result += `<span style="color:${param.color}">●</span> ${param.seriesName}: ${formatMillisecondsAxis(param.value)}<br/>`;
                    }
                });
                result += "</div>";
                return result;
            },
        });

        // Add axis pointer configuration for highlighting
        this.option.axisPointer = {
            link: {xAxisIndex: 'all'},
            label: {
                backgroundColor: 'rgba(30,30,30,0.92)',
                color: '#fff',
                fontFamily: 'inherit'
            }
        };
        
        debugLog(this.option);
        this.option && this.chart.setOption(this.option, true);

        debugLog(payload);
    }
}
